import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAppAdminStore } from './useAppAdminStore';
import * as adminAPIs from './appAndWorkflowApi';
import useAuthToken from "../../../hooks/useAuthToken";

export function useGetAllApplications() {
    return useQuery(
        'getAllApplications',
        () => adminAPIs.fetchAdminApplications(),
    );
}

export function useSearchUserIds() {
    const searchQuery = useAppAdminStore(
        (state) => state.searchQuery
    );
    return useQuery(
        ['searchByUserId', searchQuery],
        () => adminAPIs.searchByUserId(searchQuery),
        { enabled: !!searchQuery }
    );
}

export function useGetUserAppAndWorkflowNames() {
    const userId = useAppAdminStore(
        (state) => state.userId
    );
    return useQuery(
        ['getUserAppAndWorkflowNames', userId],
        () => adminAPIs.getUserAppAndWorkflowNames(userId),
        { enabled: !!userId }
    );
}

export function useGetAllWorkflows() {
    const token = useAuthToken();
    return useQuery(
        'getAllWorkflows',
        () => adminAPIs.fetchAdminWorkflows() ,{enabled: !!token}
    );
}

export function useApplicationCategory() {
    const token = useAuthToken();
    const { data } = useQuery('applicationCategories', () => adminAPIs.fetchApplicationCategory(), {
        enabled: !!token,
    });
    return { data };
}


export function useCreateNewApplication({ onSuccess, onError }:any) {

    const queryClient = useQueryClient();

    const getRequestBody = (model:any) =>{
        const updateReqBody = {
                name: model.name,
                url: model.url,
                description: model.description,
                icon: model.icon,
                iconText: model.iconText,
                isUnrestricted: model.isUnrestricted,
                isActive: model.isActive,
                isEnabled: model.isActive,
                logoutUrl: model.logoutUrl,
                appCategory: model.appCategory,
                cdkuUrl: model.cdkuUrl,
                appId:model.appId,
                openInNewTab:model.openInNewTab,
                redirectToUnify: model.redirectToUnify ?? true
            }
        return updateReqBody;
    }

    return useMutation<Record<any, any>[],
        Error,
        void,
        unknown>((model) => adminAPIs.createNewApplication(
            getRequestBody(model)), {
            onSuccess: () => {
                queryClient.invalidateQueries(['getAllApplications']);
                onSuccess();
            },
            onError: (error: any) => {
                console.error('Failed to create a new application:', error);
                onError();
            },
            mutationKey: 'createNewApplication',
        })
}

export function useUpdateApplicationDetails({ onSuccess, onError }: any) {

    const queryClient = useQueryClient();
   
    const getRequestBody = (model:any) =>{
        const updateReqBody: any = {
                name: model.name,
                url: model.url,
                description: model.description,
                icon: model.icon,
                iconText: model.iconText,
                isUnrestricted: model.isUnrestricted,
                isActive: model.isActive,
                logoutUrl: model.logoutUrl,
                appCategory: model.appCategory,
                cdkuUrl: model.cdkUUrl,
                openInNewTab: model.openInNewTab,
                redirectToUnify: model.redirectToUnify ?? true,
            }
        if (model.newStatusStartDate && model.newStatusEndDate) {
            updateReqBody.newStatusStartDate = model.newStatusStartDate;
            updateReqBody.newStatusEndDate = model.newStatusEndDate;
        }
        return updateReqBody;
    }


    return useMutation<Record<any, any>[],
        Error,
        void,
        unknown>((model: any) => adminAPIs.updateAdminApplication(
            model.appId,
            getRequestBody(model)), {
            onSuccess: () => {
                queryClient.invalidateQueries(['getAllApplications']);
                onSuccess();
                //setAdminSideSheetCollapse(true)
                //setCustomToastContent({ type: 'Success', message: 'Application Details have been updated successfully!' })
                //setShowCustomContentToast(true)
            },
            onError: (error: any) => {
                onError()
                //setAdminSideSheetCollapse(true)
                //console.error('Failed to update application:', error);
                //setCustomToastContent({ type: 'Error', message: 'Failed to update application' })
                //setShowCustomContentToast(true)
            },
            mutationKey: 'updateAdminApplicationDetails',
        })

}

export function useUpdateWorkflowDetails({ onSuccess, onError }: any) {

    const queryClient = useQueryClient();
    
    const getRequestBody = (model:any) =>{
    const updateReqBody: any = {
            name: model.name,
            url: model.url,
            description: model.description,
            icon: model.icon,
            iconText: model.iconText,
            isUnrestricted: model.isUnrestricted,
            isActive: model.isActive,
            logoutUrl: model.logoutUrl,
            appCategory: model.appCategory,
            cdkuUrl: model.cdkUUrl,
            openInNewTab: model.openInNewTab,
            redirectToUnify: model.redirectToUnify ?? true
        }
          if (model.newStatusStartDate && model.newStatusEndDate) {
            updateReqBody.newStatusStartDate = model.newStatusStartDate;
            updateReqBody.newStatusEndDate = model.newStatusEndDate;
        }
        return updateReqBody;
    }

    return useMutation<Record<any, any>[],
        Error,
        void,
        unknown>((model:any) => adminAPIs.updateAdminWorkflow(
            model.workflowId,
            // @ts-ignore
            getRequestBody(model)), {
            onSuccess: () => {
                queryClient.invalidateQueries(['getAllWorkflows']);
                onSuccess();
            },
            onError: (error: any) => {
                console.error('Failed to update workflow:', error);
                onError();
            },
            mutationKey: 'useUpdateWorfklowDetails',
        })

}

export function useGetAdminPermissions() {
    return useQuery(
        'getAdminPermissions',
        () => adminAPIs.fetchAdminPermissions(),
    );
}

export function useUpdateAdminPermissions() {
    const queryClient = useQueryClient();

    const getPermission = (permissionModel:any) => {
        return {
            type: permissionModel.type,
            enterprise: permissionModel.enterprise,
            enterprises: permissionModel.enterprises,
            stores: permissionModel.stores,
            roles:permissionModel.roles,
            users: permissionModel.users,
            id: permissionModel.appId,
        }
    }

    return useMutation<Record<any, any>[],
        Error,
        void,
        unknown>((permissionModel:any) => adminAPIs.updateAdminPermissions(
            permissionModel.appId,
            '',
            getPermission(permissionModel)
        ), {
            onSuccess: () => {
                queryClient.invalidateQueries(['getAdminPermissions']);
            },
            onError: (error: any) => {
                console.error('Failed to update application:', error);
            },
            mutationKey: 'useUpdateAdminPermissions',
        })
}


export function useUpdateAdminWorkflowPermissions() {
    const queryClient = useQueryClient();

    const getPermission = (permissionModel:any) => {
        return {
            type: permissionModel.type,
            enterprise: permissionModel.enterprise,
            enterprises: permissionModel.enterprises,
            stores: permissionModel.stores,
            roles:permissionModel.roles,
            users: permissionModel.users,
            id: permissionModel.workflowId,
        }
    }

    return useMutation<Record<any, any>[],
        Error,
        void,
        unknown>((permissionModel:any) => adminAPIs.updateAdminPermissions(
            permissionModel.workflowId,
            '',
            getPermission(permissionModel)
        ), {
            onSuccess: () => {
                queryClient.invalidateQueries(['getAdminPermissions']);
            },
            onError: (error: any) => {
                console.error('Failed to update workflow permission:', error);
            },
            mutationKey: 'useUpdateAdminWorkflowPermissions',
        })
}